<template>
    <div>
        <div class="d-flex flex-wrap align-items-center format-summary">
            <div class="mr-auto pr-3">
                <div class="font-weight-semibold">{{ currentLabel }}</div>
                <small v-if="value" class="text-muted">
                    {{ value.w }} × {{ value.h }} mm · Tryckyta {{ value.print_box.x2 - value.print_box.x1 | formatMillimetres }} ×
                    {{ value.print_box.y2 - value.print_box.y1 | formatMillimetres }} mm · {{ value.barcode_boxes.length }} streckkodsrutor
                </small>
            </div>
            <button type="button" class="btn btn-light btn-sm" :disabled="disabled || !presets.length" @click="open">
                <i class="icon-pencil mr-2" aria-hidden="true"></i>Redigera tryckformat
            </button>
        </div>
        <b-modal v-model="showing" title="Redigera tryckformat" size="xl" ok-title="Använd värden" cancel-title="Avbryt" @ok="apply">
            <template v-if="draft">
                <div class="form-group">
                    <label for="series-print-source">Snabbval</label>
                    <select id="series-print-source" v-model="draft.source" class="form-control" @change="selectPreset">
                        <option v-for="preset in presets" :key="preset.id" :value="preset.id">{{ preset.label }}</option>
                        <option value="manual">Manuellt format</option>
                    </select>
                    <small class="form-text text-muted">Snabbval kopierar alla mått. Ändrar du ett värde blir formatet manuellt. Alla mått anges i millimeter.</small>
                </div>
                <div v-if="errors.length" class="alert alert-danger" role="alert">
                    <div v-for="message in errors" :key="message">{{ message }}</div>
                </div>
                <section class="format-section">
                    <h6><i class="icon-file-empty mr-2" aria-hidden="true"></i>Sidstorlek</h6>
                    <div class="row">
                        <div v-for="field in pageFields" :key="field.key" class="col-sm-6 form-group mb-0">
                            <label :for="'series-page-' + field.key">{{ field.label }}</label>
                            <div class="input-group">
                                <input :id="'series-page-' + field.key" v-model.number="draft[field.key]" class="form-control" type="number" min="0" step="any" @input="manual" />
                                <div class="input-group-append"><span class="input-group-text">mm</span></div>
                            </div>
                        </div>
                    </div>
                </section>
                <section class="format-section">
                    <h6><i class="icon-crop mr-2" aria-hidden="true"></i>Tryckyta</h6>
                    <p class="text-muted small">Koordinaterna räknas från sidans övre vänstra hörn. X1/Y1 är startpunkten och X2/Y2 är slutpunkten.</p>
                    <div class="row">
                        <div v-for="field in printFields" :key="field.key" class="col-6 col-md-3 form-group mb-0">
                            <label :for="'series-print-' + field.key">{{ field.label }}</label>
                            <div class="input-group">
                                <input :id="'series-print-' + field.key" v-model.number="draft.print_box[field.key]" class="form-control" type="number" min="0" step="any" @input="manual" />
                                <div class="input-group-append"><span class="input-group-text">mm</span></div>
                            </div>
                        </div>
                    </div>
                </section>
                <section class="format-section">
                    <div class="d-flex align-items-center justify-content-between mb-3">
                        <h6 class="mb-0"><i class="icon-barcode2 mr-2" aria-hidden="true"></i>Streckkodsrutor</h6>
                        <button type="button" class="btn btn-light btn-sm" :disabled="draft.barcode_boxes.length >= 50" @click="addBox"><i class="icon-plus2 mr-1" aria-hidden="true"></i>Lägg till ruta</button>
                    </div>
                    <p v-if="!draft.barcode_boxes.length" class="text-muted mb-0">Inga streckkodsrutor angivna.</p>
                    <div v-for="(box, index) in draft.barcode_boxes" :key="index" class="barcode-box">
                        <div class="d-flex align-items-center mb-3">
                            <strong class="mr-auto">Ruta {{ index + 1 }}</strong>
                            <label class="mb-0 mr-3"><input v-model="box.barcode" type="checkbox" class="mr-1" @change="manual" />Visa streckkod</label>
                            <button type="button" class="btn btn-sm btn-outline-danger btn-icon" :aria-label="'Ta bort ruta ' + (index + 1)" title="Ta bort ruta" @click="removeBox(index)">
                                <i class="icon-bin" aria-hidden="true"></i>
                            </button>
                        </div>
                        <div class="row">
                            <div v-for="field in barcodeFields" :key="field.key" class="col-6 col-md form-group mb-0">
                                <label :for="'series-barcode-' + index + '-' + field.key">{{ field.label }}</label>
                                <div class="input-group input-group-sm">
                                    <input :id="'series-barcode-' + index + '-' + field.key" v-model.number="box[field.key]" class="form-control" type="number" min="0" step="any"
                                        :placeholder="field.placeholder" @input="manual" />
                                    <div class="input-group-append"><span class="input-group-text">mm</span></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <small class="form-text text-muted mt-2">Tom bredd, höjd och streckkodshöjd använder standardvärdena 160, 10 respektive 5 mm.</small>
                </section>
                <p class="text-muted small mb-0">”Använd värden” uppdaterar formuläret. Spara därefter kortserien med den vanliga Spara-knappen.</p>
            </template>
        </b-modal>
    </div>
</template>

<script>
import { printFormatErrors, cleanPrintFormat } from '@services/CardSeriePrintFormat';

export default {
    name: 'CardSeriePrintFormat',
    props: {
        value: { type: Object, default: null },
        presets: { type: Array, default: () => [] },
        disabled: { type: Boolean, default: false },
    },
    data: () => ({
        showing: false,
        draft: null,
        errors: [],
        pageFields: [{ key: 'w', label: 'Bredd' }, { key: 'h', label: 'Höjd' }],
        printFields: [{ key: 'x1', label: 'X1' }, { key: 'x2', label: 'X2' }, { key: 'y1', label: 'Y1' }, { key: 'y2', label: 'Y2' }],
        barcodeFields: [
            { key: 'x', label: 'X', placeholder: '' },
            { key: 'y', label: 'Y', placeholder: '' },
            { key: 'w', label: 'Bredd', placeholder: '160' },
            { key: 'h', label: 'Höjd', placeholder: '10' },
            { key: 'barcode_h', label: 'Streckkodshöjd', placeholder: '5' },
        ],
    }),
    filters: {
        formatMillimetres(value) { return Number(value.toFixed(2)); },
    },
    computed: {
        currentLabel() {
            if (!this.value) return 'Ej angivet';
            const preset = this.presets.find((item) => item.id === this.value.source);
            return preset ? preset.label : 'Manuellt format';
        },
    },
    methods: {
        open() {
            this.draft = JSON.parse(JSON.stringify(this.value || this.presets[0].format));
            this.errors = [];
            this.showing = true;
        },
        selectPreset() {
            const preset = this.presets.find((item) => item.id === this.draft.source);
            if (preset) this.draft = JSON.parse(JSON.stringify(preset.format));
            this.errors = [];
        },
        manual() { this.draft.source = 'manual'; this.errors = []; },
        addBox() {
            this.draft.barcode_boxes.push({ x: 0, y: 0, barcode: true });
            this.manual();
        },
        removeBox(index) { this.draft.barcode_boxes.splice(index, 1); this.manual(); },
        apply(event) {
            event.preventDefault();
            this.errors = printFormatErrors(this.draft, this.presets);
            if (this.errors.length) return;
            this.$emit('input', cleanPrintFormat(this.draft));
            this.showing = false;
        },
    },
};
</script>

<style scoped>
.format-summary { gap: 8px; }
.format-section { border: 1px solid #e5e8eb; border-radius: 5px; padding: 16px; margin-bottom: 16px; }
.format-section h6 { font-size: 13px; font-weight: 600; }
.format-section h6 i { color: #687582; }
.format-section label { font-size: 12px; }
.barcode-box { padding: 12px; background: #f8f9fa; border: 1px solid #e5e8eb; border-radius: 4px; }
.barcode-box + .barcode-box { margin-top: 10px; }
.format-section .input-group-text { color: #79838c; padding-left: 8px; padding-right: 8px; }
@media (max-width: 767px) {
    .format-section .form-group { margin-bottom: 10px !important; }
}
</style>
